/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { func, bool } from 'prop-types'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/Drawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import {
  divider,
  text,
  list,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_SHOPIFY_SEND_INVOICE } from 'ducks/drawers/types'
import { selectIntegrationCredentialsForId } from 'ducks/integrations'

import doPreviewInvoice from 'ducks/integrations/shopify/operations/doPreviewInvoice'
import doSendInvoice from 'ducks/integrations/shopify/operations/doSendInvoice'

import { styles } from './ReviewInvoice.styles'

const ReviewInvoice = ({
  drawerResourceId,
  drawerIntegrationId,
  open = true,
  data,
  onClose,
  onExit,
  onHide,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [iframeContent, setIframeContent] = useState(null)
  const isMounted = useRef(false)

  const dispatch = useDispatch()

  const integration = useSelector(state => {
    return selectIntegrationCredentialsForId(state, {
      id: drawerIntegrationId,
    })
  })

  const handleSend = useCallback(
    async () => {
      setIsSending(true)

      await dispatch(doSendInvoice(integration.id, drawerResourceId, data))

      onClose({ ignoreStack: true })
    },
    [data, dispatch, drawerResourceId, integration, onClose]
  )

  const { openDrawer } = useDrawer({
    type: DRAWER_TYPE_SHOPIFY_SEND_INVOICE,
  })

  // we've probably loaded directly - this drawer is a second step and cannot operate
  // on its own, so we need to take someone to the first step
  useEffect(
    () => {
      isMounted.current = true
      if (!data) {
        onClose()
        openDrawer(drawerResourceId)
      } else {
        dispatch(doPreviewInvoice(integration.id, drawerResourceId, data)).then(
          html => {
            if (isMounted.current) {
              setIframeContent(html)
              setIsLoading(false)
            }
          }
        )
      }
      return function cleanup() {
        isMounted.current = false
      }
    },
    [
      data,
      openDrawer,
      onClose,
      drawerResourceId,
      dispatch,
      integration,
      isMounted,
    ]
  )
  if (!data) return null

  const footer = !isLoading && (
    <ModalBtns
      saveBtnText={isSending ? 'Sending invoice...' : 'Send invoice'}
      saveBtnDisabled={isSending}
      tertiaryBtnText="Back"
      onSave={handleSend}
      onClickTertiaryBtn={onClose}
    />
  )

  return (
    <Drawer
      title="Review invoice"
      open={open}
      footer={footer}
      onClose={onExit}
      onHide={onHide}
      isLoading={isLoading}
      contentStyles={styles.container}
    >
      <div className="grui pt-10 flex flex-col flex-1" css={styles.content}>
        <ul
          className="grui flex-shrink-0"
          css={[list.styles.preflight, text.styles.textDark]}
        >
          <li>
            <span css={text.styles.textMediumDark}>From&nbsp;&nbsp;</span>
            {data.from}
          </li>
          <li>
            <span css={text.styles.textMediumDark}>To&nbsp;&nbsp;</span>
            {data.to}
          </li>
          <li>
            <span css={text.styles.textMediumDark}>Subject&nbsp;&nbsp;</span>
            {data.subject}

            <Tooltip title="Shopify variables will be interpolated when sending the invoice">
              <span css={styles.icon} className="Icon Icon-error-outline" />
            </Tooltip>
          </li>
        </ul>
        <div
          className="grui my-10"
          css={divider.styles.base}
          role="separator"
        />
        <div className="grui flex-1" css={styles.iframeWrapper}>
          <iframe
            srcDoc={iframeContent}
            sandbox
            width="100%"
            height="100%"
            title="Invoice"
          />
        </div>
      </div>
    </Drawer>
  )
}

ReviewInvoice.propTypes = {
  onClose: func,
  open: bool.isRequired,
  onHide: func,
}

ReviewInvoice.defaultProps = {
  onClose() {},
  onHide() {},
  invoice: {},
  onSaveInvoice() {},
  onShowlastStep() {},
}

export default ReviewInvoice
